import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import noop from 'lodash/noop';

import { Workflow } from 'modules/workflows/types';
import { BaseOption, BasicFilterSelect, MultiFilterSelect } from 'ui/selects';
import { pxToRem } from 'utils/convert';
import { useTriggeredJourneyContext } from 'modules/workflows/context/TriggerJourneyContext/TriggerJourneyContext';
import { TriggerJourney } from 'modules/workflows/context/TriggerJourneyContext/TriggerJourney.types';
import { WorkflowType } from 'modules/workflows/enums';
import { WorkflowLiveStatusBadge } from 'modules/workflows/components/HeaderBar/WorkflowTitle/WorkflowLiveStatusBadge';
import { ChevronDown16 } from 'ui/icons';
import { body2, spacing, textColors } from 'ui/theme';
import {
  PublishedJourney,
  fetchPublicWorkflowsQueryVariables,
  fetchPublishedWorkflowsQuery,
} from 'modules/workflowNodeEditors/components/StartNodeEditor/EntranceLimitForm/ConcurrentJourneyEntranceCriteriaUtils/PublishedJourneysContext';
import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import { WorkflowOption } from './WorkflowSelect.types';
import { parseItems } from './WorkflowSelect.utils';

export const PLACEHOLDERS = {
  MULTI_SELECT_DROPDOWN: 'Select journeys',
  DROPDOWN: 'Select a journey',
  INPUT: 'Search',
  LOADING: 'Loading...',
  NO_JOURNEYS: 'No journeys found',
};

const FullWidthSelect = styled(BasicFilterSelect)`
  div button {
    width: ${pxToRem(380)};
    > div {
      > :not(:first-child) {
        display: none;
      }
      > div {
        max-width: ${pxToRem(325)};
        white-space: nowrap;
        overflow-x: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`;

const FullWidthMultiSelect = styled(MultiFilterSelect)`
  margin-left: ${pxToRem(32)};
  max-width: ${pxToRem(400)};
  div button {
    width: ${pxToRem(380)};
    > div {
      > :not(:first-child) {
        display: none;
      }
      > div {
        max-width: ${pxToRem(325)};
        white-space: nowrap;
        overflow-x: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`;

const StyledWorkflowStatusBadge = styled(WorkflowLiveStatusBadge)`
  width: fit-content;
`;

const TriggerWrapper = styled.div`
  display: flex;
  height: ${pxToRem(36)};
  padding: 0px ${spacing.threeQuarter};
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.half};
  align-self: stretch;
`;

const TriggerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  flex: 1 0 0;
  justify-content: space-between;
  width: ${pxToRem(372)};
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const TriggerText = styled.div`
  overflow: hidden;
  color: ${textColors.textPrimary};
  text-overflow: ellipsis;
  /* body 2/400 */
  ${body2}
`;

type WorkflowSelectProps = {
  workflow: Partial<Workflow>;
  displayValue?: string;
  value?: string;
  hasError?: boolean;
  onChange?: (value?: TriggerJourney) => void;
  isReadOnly?: boolean;
  onlyPublished?: boolean;
  showStatusInTrigger?: boolean;
};

export const WorkflowSelect = ({
  displayValue,
  value,
  onChange = noop,
  isReadOnly = false,
  workflow,
  hasError = false,
  onlyPublished = false,
  showStatusInTrigger = false,
}: WorkflowSelectProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allOptions, setAllOptions] = useState<WorkflowOption[]>([]);
  const [visibleOptions, setVisibleOptions] = useState<WorkflowOption[]>([]);
  const { triggerJourneys, loading, error, called, refetch, loadTriggerJourneys } =
    useTriggeredJourneyContext();

  useEffect(() => {
    setAllOptions(parseItems(triggerJourneys, workflow));
  }, [triggerJourneys]);

  // refetch the triggered journeys when the passed in the value has changed.
  useEffect(() => {
    if (!called) {
      loadTriggerJourneys?.();
    } else {
      refetch?.();
    }
  }, [value]);

  useEffect(() => {
    if (searchTerm) {
      setVisibleOptions(
        allOptions.filter(({ data }) =>
          data?.name.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    } else {
      setVisibleOptions(allOptions);
    }
  }, [allOptions, searchTerm, value]);

  const getPlaceholder = () => {
    if (loading) return PLACEHOLDERS.LOADING;
    if (displayValue) return displayValue;
    return PLACEHOLDERS.DROPDOWN;
  };

  const filteredVisibleOptions = onlyPublished
    ? visibleOptions.filter((option) => option.data?.workflowType === WorkflowType.PUBLISHED)
    : visibleOptions;
  const selectedOption = filteredVisibleOptions.find((option) => option.value === value);
  const additionalProps =
    showStatusInTrigger && selectedOption
      ? {
          Trigger: () => (
            <TriggerWrapper>
              <TriggerContainer>
                <TriggerText>{selectedOption.data.name}</TriggerText>
                {selectedOption?.data?.enabled !== undefined && (
                  <StyledWorkflowStatusBadge
                    isWorkflowEnabled={selectedOption?.data?.enabled}
                    workflowType={selectedOption?.data?.workflowType}
                  />
                )}
              </TriggerContainer>

              <ChevronDown16 />
            </TriggerWrapper>
          ),
        }
      : {};
  return (
    <FullWidthSelect
      id="workflow-select"
      defaultValues={value ? [value] : []}
      isReadOnly={isReadOnly || loading}
      onSelect={(val, args) => {
        onChange(args.selectedOptions[0]?.data as TriggerJourney);
      }}
      onSearchChange={(st: string | undefined) => {
        setSearchTerm(st || '');
      }}
      disableInternalFiltering
      options={filteredVisibleOptions as BaseOption<string>[]}
      placeholder={getPlaceholder()}
      data-test={`workflow-trigger-select`}
      isError={!!hasError || !!error}
      isLoading={loading}
      emptyStateText={PLACEHOLDERS.NO_JOURNEYS}
      isFullWidth
      {...additionalProps}
    />
  );
};

type PublishedWorkflowsMultiSelectProps = {
  workflow: Partial<Workflow>;
  displayValue?: string;
  value?: string;
  hasError?: boolean;
  onChange?: (value?: number[]) => void;
  isReadOnly?: boolean;
  onlyPublished?: boolean;
  showStatusInTrigger?: boolean;
  journeys?: number[];
};

export const PublishedWorkflowsMultiSelect = ({
  displayValue,
  value,
  onChange = noop,
  isReadOnly = false,
  workflow,
  hasError = false,
  onlyPublished = false,
  showStatusInTrigger = false,
  journeys = [],
}: PublishedWorkflowsMultiSelectProps) => {
  /* brief description of the logic */
  //get 50 journeys
  //get selected journeys if they exist
  //merge them then sort
  //set them as allOptions

  //another gql for the search term to keep it clean
  //when we get the results, we have to add them to allOptions
  // and then we set them to visible options as long as the user has a searchTerm

  const [searchTerm, setSearchTerm] = useState('');
  const [allOptions, setAllOptions] = useState<WorkflowOption[]>([]);
  const [visibleOptions, setVisibleOptions] = useState<WorkflowOption[]>([]);

  const [
    loadRandomJourneys,
    {
      data: randomData,
      called: randomCalled,
      error: randomError,
      loading: randomLoading,
      refetch: randomRefetch,
    },
  ] = useLazyQuery(fetchPublishedWorkflowsQuery, {
    variables: fetchPublicWorkflowsQueryVariables(),
  });

  const [
    loadSelectedJourneys,
    {
      data: selectedData,
      called: selectedCalled,
      error: selectedError,
      loading: selectedLoading,
      refetch: selectedRefetch,
    },
  ] = useLazyQuery(fetchPublishedWorkflowsQuery, {
    variables: fetchPublicWorkflowsQueryVariables(),
  });

  const [
    loadSearchTermJourneys,
    {
      data: searchedData,
      called: searchedCalled,
      error: searchedError,
      loading: searchedLoading,
      refetch: searchedRefetch,
    },
  ] = useLazyQuery(fetchPublishedWorkflowsQuery, {
    variables: fetchPublicWorkflowsQueryVariables(),
  });

  useEffect(() => {
    loadRandomJourneys?.();
    if (journeys.length > 0) {
      loadSelectedJourneys({
        variables: fetchPublicWorkflowsQueryVariables('', journeys),
      });
    }
  }, []);

  useEffect(() => {
    const randomJourneys =
      ((randomData as any)?.workflows?.results as PublishedJourney[]) || ([] as PublishedJourney[]);
    const selectedJourneys =
      ((selectedData as any)?.workflows?.results as PublishedJourney[]) ||
      ([] as PublishedJourney[]);
    const mergedJourneys = [...randomJourneys, ...selectedJourneys];
    //remove duplicates then sort
    const uniqueJourneys = mergedJourneys
      .filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
      .sort();
    setAllOptions(parseItems(uniqueJourneys, workflow));
  }, [randomData, selectedData]);

  useEffect(() => {
    if (searchTerm) {
      if (!searchedCalled) {
        loadSearchTermJourneys({
          variables: fetchPublicWorkflowsQueryVariables(searchTerm),
        });
      } else {
        searchedRefetch(fetchPublicWorkflowsQueryVariables(searchTerm));
      }
    }
  }, [searchTerm]);

  const searchedJourneys = useMemo(() => {
    return (
      ((searchedData as any)?.workflows?.results as PublishedJourney[]) ||
      ([] as PublishedJourney[])
    );
  }, [searchedData]);

  useEffect(() => {
    setAllOptions((options) => {
      const mergedOptions = [...options, ...parseItems(searchedJourneys, workflow)];
      const uniqueOptions = mergedOptions
        .filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
        .sort();
      return uniqueOptions;
    });
  }, [searchedData]);

  useEffect(() => {
    if (searchTerm !== '') {
      setVisibleOptions(parseItems(searchedJourneys, workflow));
    } else {
      setVisibleOptions(allOptions);
    }
  }, [searchedData, searchTerm, allOptions]);

  const getPlaceholder = () => {
    if (randomLoading) return PLACEHOLDERS.LOADING;
    if (displayValue) return displayValue;
    return PLACEHOLDERS.MULTI_SELECT_DROPDOWN;
  };

  const filteredVisibleOptions = visibleOptions;
  const selectedOption = filteredVisibleOptions.find((option) => option.value === value);
  const additionalProps =
    showStatusInTrigger && selectedOption
      ? {
          Trigger: () => (
            <TriggerWrapper>
              <TriggerContainer>
                <TriggerText>{selectedOption.data.name}</TriggerText>
                {selectedOption?.data?.enabled !== undefined && (
                  <StyledWorkflowStatusBadge
                    isWorkflowEnabled={selectedOption?.data?.enabled}
                    workflowType={selectedOption?.data?.workflowType}
                  />
                )}
              </TriggerContainer>

              <ChevronDown16 />
            </TriggerWrapper>
          ),
        }
      : {};

  const seletedValues = allOptions
    ?.filter((item) => journeys.indexOf(item.data.id) !== -1)
    .map((i) => i.data?.id?.toString());

  return (
    <FullWidthMultiSelect
      id="published-workflows-multi-select"
      defaultValues={seletedValues /*seletedValues as unknown as SelectValueType[]*/}
      isReadOnly={isReadOnly || randomLoading}
      onSelect={(val, args) => {
        // if val.length != journeyIds.length -> that means it's the initial load so the whole option list might not be populated
        const journeyIds = args?.selectedOptions
          ?.map((o) => o.data?.id as number)
          .filter((o) => o !== undefined);
        if (val.length === journeyIds.length) {
          onChange(journeyIds.length > 0 ? journeyIds : undefined);
        }
      }}
      onSearchChange={debounce((st: string | undefined) => setSearchTerm(st || ''), 500)}
      disableInternalFiltering
      options={filteredVisibleOptions as BaseOption<string>[]}
      placeholder={getPlaceholder()}
      data-test={`published-workflows-multi-select`}
      isError={!!hasError || !!randomError}
      isLoading={randomLoading}
      emptyStateText={PLACEHOLDERS.NO_JOURNEYS}
      isFullWidth
      selectValueDisplayText={`${seletedValues.length} journeys selected`}
      {...additionalProps}
    />
  );
};
